import { useState } from "react";
import { recipes } from "../data/mockData";

export default function Recipes() {
  const [search, setSearch] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const cuisines = ["All", ...new Set(recipes.map((r) => r.cuisine))];

  const filtered = recipes.filter((r) => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase()) || r.cuisine.toLowerCase().includes(search.toLowerCase());
    const matchCuisine = cuisineFilter === "All" || r.cuisine === cuisineFilter;
    return matchSearch && matchCuisine;
  });

  if (selected) return <RecipeDetail recipe={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="page">
      <h1 className="page-title">📖 Recipes</h1>
      <p className="page-subtitle">Find something delicious to cook today.</p>

      {/* Search & filter bar */}
      <div style={styles.searchBar}>
        <input type="text" placeholder="Search dishes or cuisines..." value={search} onChange={(e) => setSearch(e.target.value)} style={styles.searchInput} />
        <div style={styles.cuisineFilters}>
          {cuisines.map((c) => (
            <button key={c} onClick={() => setCuisineFilter(c)} style={{ ...styles.filterBtn, ...(cuisineFilter === c ? styles.filterActive : {}) }}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Recipe cards */}
      <div style={styles.grid}>
        {filtered.map((r) => (
          <div key={r.id} className="card" style={styles.card} onClick={() => setSelected(r)}>
            <div style={styles.imageWrap}>
              <img src={r.image} alt={r.name} style={styles.image} />
              <span className="tag" style={styles.cuisineTag}>{r.cuisine}</span>
            </div>
            <div style={styles.cardBody}>
              <h3 style={styles.recipeName}>{r.name}</h3>
              <p style={styles.recipeDesc}>{r.description}</p>
              <div style={styles.meta}>
                <span style={styles.metaItem}>⏱ {r.time}</span>
                <span style={styles.metaItem}>📊 {r.difficulty}</span>
                <span style={styles.metaItem}>🥘 {r.ingredients.length} items</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={styles.empty}>No recipes found. Try a different search! 🍽️</div>
      )}
    </div>
  );
}

function RecipeDetail({ recipe, onBack }) {
  return (
    <div className="page">
      <button onClick={onBack} style={styles.backBtn}>← Back to Recipes</button>

      <div style={styles.detailWrap}>
        {/* Left: Ingredients */}
        <div className="card" style={styles.leftPanel}>
          <div style={styles.bookHeader}>
            <div style={styles.recipeEmoji}>{recipe.emoji}</div>
            <h2 style={styles.detailTitle}>{recipe.name}</h2>
            <p style={styles.detailDesc}>{recipe.description}</p>
            <div style={styles.meta}>
              <span style={styles.metaItem}>⏱ {recipe.time}</span>
              <span style={styles.metaItem}>📊 {recipe.difficulty}</span>
              <span style={styles.metaItem}>🌍 {recipe.cuisine}</span>
            </div>
          </div>

          <div style={styles.divider} />

          <h3 style={styles.panelTitle}>🧺 Ingredients</h3>
          <ul style={styles.ingredientList}>
            {recipe.ingredients.map((ing, i) => (
              <li key={i} style={styles.ingredient}>
                <span style={styles.ingredientDot}>•</span>
                <span style={styles.ingredientName}>{ing.name}</span>
                <span style={styles.ingredientQty}>{ing.quantity}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: Steps */}
        <div className="card" style={styles.rightPanel}>
          <img src={recipe.image} alt={recipe.name} style={styles.detailImage} />
          <h3 style={{ ...styles.panelTitle, marginTop: "1.5rem" }}>👨‍🍳 How to Cook</h3>
          <ol style={styles.stepList}>
            {recipe.steps.map((step, i) => (
              <li key={i} style={styles.step}>
                <div style={styles.stepNum}>{i + 1}</div>
                <p style={styles.stepText}>{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

const styles = {
  searchBar: { display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "2rem" },
  searchInput: { fontSize: "0.95rem" },
  cuisineFilters: { display: "flex", gap: "0.5rem", flexWrap: "wrap" },
  filterBtn: { padding: "0.35rem 0.9rem", borderRadius: "50px", border: "1.5px solid #e0cdb0", background: "#fff9f2", color: "#9b7b54", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", transition: "all 0.2s" },
  filterActive: { background: "#f0e8d8", color: "#6b4f2e", borderColor: "#c4a882" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "1.25rem" },
  card: { cursor: "pointer", padding: 0, overflow: "hidden" },
  imageWrap: { position: "relative" },
  image: { width: "100%", height: "160px", objectFit: "cover" },
  cuisineTag: { position: "absolute", top: "10px", right: "10px" },
  cardBody: { padding: "1.1rem 1.25rem 1.25rem" },
  recipeName: { fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#3d2b1a", marginBottom: "0.3rem" },
  recipeDesc: { color: "#9b7b54", fontSize: "0.85rem", lineHeight: 1.5, marginBottom: "0.75rem" },
  meta: { display: "flex", gap: "0.75rem", flexWrap: "wrap" },
  metaItem: { fontSize: "0.8rem", color: "#c4a882", fontWeight: 700 },
  empty: { textAlign: "center", color: "#9b7b54", padding: "3rem", fontStyle: "italic" },

  // Detail
  backBtn: { background: "none", border: "none", color: "#9b7b54", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", marginBottom: "1.5rem", padding: 0 },
  detailWrap: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" },
  leftPanel: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  rightPanel: { display: "flex", flexDirection: "column" },
  bookHeader: { textAlign: "center" },
  recipeEmoji: { fontSize: "3rem", marginBottom: "0.5rem" },
  detailTitle: { fontFamily: "'Playfair Display', serif", fontSize: "1.6rem", color: "#3d2b1a" },
  detailDesc: { color: "#9b7b54", fontSize: "0.9rem", margin: "0.5rem 0 0.75rem" },
  divider: { height: "1px", background: "#e0cdb0", margin: "1rem 0" },
  panelTitle: { fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#3d2b1a", marginBottom: "0.75rem" },
  ingredientList: { listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" },
  ingredient: { display: "flex", alignItems: "center", gap: "0.5rem" },
  ingredientDot: { color: "#c4a882", fontWeight: 700 },
  ingredientName: { flex: 1, color: "#3d2b1a", fontSize: "0.9rem" },
  ingredientQty: { color: "#9b7b54", fontWeight: 700, fontSize: "0.85rem" },
  detailImage: { width: "100%", height: "200px", objectFit: "cover", borderRadius: "10px" },
  stepList: { listStyle: "none", display: "flex", flexDirection: "column", gap: "1rem" },
  step: { display: "flex", gap: "1rem", alignItems: "flex-start" },
  stepNum: { minWidth: "28px", height: "28px", borderRadius: "50%", background: "#f0e8d8", color: "#6b4f2e", fontWeight: 700, fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center" },
  stepText: { color: "#3d2b1a", fontSize: "0.9rem", lineHeight: 1.6 },
};
